import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../api/context';
import { ApiError } from '../api/client';
import { useInvalidateResource } from '../api/hooks';
import { can, type ActionInput, type ActionMeta, type ResourceMeta } from '../meta/types';
import { Button } from '../design/primitives/Button';
import { ConfirmDialog, Dialog, DropdownMenu, type ConfirmSpec, type MenuItemDef } from '../design/primitives/Overlay';
import { Field, Input, Select, Textarea } from '../design/primitives/Input';
import { DatePicker } from '../design/primitives/DatePicker';
import { useToast } from '../design/primitives/Toast';
import type { IconName } from '../design/icons';
import type { ActionDef, ScreenContext } from './types';

// Actions: merge ScreenDef.actions over ResourceMeta.actions, filter by scope/`when`/RBAC,
// then run them — confirm dialog when `confirm`, a small JSON-schema form when `input`,
// then POST via the client. Custom actions supply `run` or `component` instead.

export type ResolvedAction = ActionMeta & Pick<ActionDef, 'run' | 'component' | 'placement' | 'openParam' | 'hidden'>;

export function resolveActions(meta: ResourceMeta | undefined, screenActions: ActionDef[] | undefined, scope: 'collection' | 'item', row?: Record<string, unknown>): ResolvedAction[] {
  const base: ResolvedAction[] = (meta?.actions ?? []).filter((a) => a.method !== 'GET').map((a) => ({ ...a }));
  // Screen-only actions (a `component` or `run` with no meta counterpart — "Invite someone" on
  // the members list, "Import CSV") are not resource permissions: the meta cannot vouch for
  // them, so they are gated by their own `permission` (against the resource's set) or shown.
  const screenOnly = new Set<string>();
  for (const s of screenActions ?? []) {
    const i = base.findIndex((b) => b.name === s.name);
    if (i >= 0) base[i] = { ...base[i]!, ...s } as ResolvedAction;
    else {
      screenOnly.add(s.name);
      base.push({ method: 'POST', path: s.scope === 'collection' ? s.name : `:id/${s.name}`, scope: s.scope ?? scope, label: s.name, ...s } as ResolvedAction);
    }
  }
  return base.filter((a) => {
    if (a.hidden) return false;
    if (a.scope !== scope) return false;
    if (screenOnly.has(a.name)) {
      if (a.permission && meta?.permissions?.length && !meta.permissions.includes(a.permission) && !meta.permissions.includes('*')) return false;
    } else if (meta && !can(meta, a.name)) return false;
    if (a.when && row) {
      for (const [k, allowed] of Object.entries(a.when)) if (!allowed.includes(row[k])) return false;
    }
    return true;
  });
}

function confirmSpec(a: ResolvedAction): ConfirmSpec | null {
  if (!a.confirm) return null;
  if (typeof a.confirm === 'string') return { title: a.label, body: a.confirm, confirmLabel: a.label, danger: a.tone === 'danger' };
  return { title: a.confirm.title, body: a.confirm.body, confirmLabel: a.confirm.confirmLabel ?? a.label, danger: a.tone === 'danger', reason: a.confirm.reason };
}

function InputForm({ schema, onSubmit, onCancel, busy, label }: { schema: ActionInput; onSubmit: (body: Record<string, unknown>) => void; onCancel: () => void; busy: boolean; label: string }) {
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const v: Record<string, unknown> = {};
    for (const [k, p] of Object.entries(schema.properties)) if (p.default !== undefined) v[k] = p.default;
    return v;
  });
  const set = (k: string, v: unknown) => setValues((s) => ({ ...s, [k]: v }));
  const required = new Set(schema.required ?? []);
  const valid = [...required].every((k) => values[k] != null && values[k] !== '');
  let first = true;
  return (
    <form
      className="hf-stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) onSubmit(values);
      }}
    >
      {Object.entries(schema.properties).map(([k, p]) => {
        const focus = first;
        first = false;
        const title = p.title ?? k.replace(/_/g, ' ');
        const v = values[k];
        return (
          <Field key={k} label={title} help={p.description} required={required.has(k)}>
            {p.enum ? (
              <Select value={(v as string) ?? ''} onChange={(e) => set(k, e.target.value || null)} placeholder="Select…" options={p.enum.map((o) => ({ value: o, label: o.replace(/_/g, ' ') }))} autoFocus={focus} />
            ) : p.format === 'date' ? (
              <DatePicker value={(v as string) ?? null} onChange={(iso) => set(k, iso)} autoFocus={focus} />
            ) : p.type === 'integer' || p.type === 'number' ? (
              <Input inputMode="decimal" numeric value={v == null ? '' : String(v)} onChange={(e) => set(k, e.target.value === '' ? null : Number(e.target.value))} autoFocus={focus} />
            ) : p.type === 'boolean' ? (
              <Select value={v == null ? '' : v ? 'true' : 'false'} onChange={(e) => set(k, e.target.value === '' ? null : e.target.value === 'true')} placeholder="—" options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} autoFocus={focus} />
            ) : p.format === 'textarea' ? (
              <Textarea value={(v as string) ?? ''} onChange={(e) => set(k, e.target.value)} autoFocus={focus} />
            ) : (
              <Input value={(v as string) ?? ''} onChange={(e) => set(k, e.target.value)} autoFocus={focus} />
            )}
          </Field>
        );
      })}
      <div className="hf-form-actions">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" busy={busy} disabled={!valid}>
          {label}
        </Button>
      </div>
    </form>
  );
}

export interface RunContext {
  id?: string;
  row?: Record<string, unknown>;
  selected?: string[];
  refresh?: () => void;
}

/** Runs actions for one resource; render `dialogs` once in the screen. */
export function useActionRunner(resource: string, opts: { onDone?: (action: ResolvedAction, result: unknown, ctx: RunContext) => void } = {}) {
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const invalidate = useInvalidateResource();
  const [pending, setPending] = useState<{ action: ResolvedAction; ctx: RunContext; stage: 'confirm' | 'input'; reason?: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const execute = useCallback(
    async (action: ResolvedAction, ctx: RunContext, body: Record<string, unknown> = {}) => {
      setBusy(true);
      try {
        let result: unknown;
        if (action.run) {
          const sc: ScreenContext & { navigate: (to: string) => void; selected?: string[] } = { resource, id: ctx.id, row: ctx.row, refresh: () => invalidate(resource, ctx.id), navigate: (to) => navigate(to), selected: ctx.selected };
          result = await action.run(sc);
        } else if (ctx.selected && ctx.selected.length && action.scope === 'item') {
          // Bulk over selected rows: one call per id (no bulk endpoint assumed).
          const failed: string[] = [];
          for (const id of ctx.selected) {
            try {
              await api.resource(resource).action(action.name, { id, body, method: action.method });
            } catch {
              failed.push(id);
            }
          }
          result = { ok: ctx.selected.length - failed.length, failed };
          if (failed.length) toast({ kind: 'error', title: `${action.label}: ${failed.length} of ${ctx.selected.length} failed` });
        } else {
          result = await api.resource(resource).action(action.name, { id: ctx.id, body, method: action.method });
        }
        invalidate(resource, ctx.id);
        ctx.refresh?.();
        if (!action.run) toast({ title: `${action.label} — done` });
        opts.onDone?.(action, result, ctx);
        setPending(null);
        return result;
      } catch (e) {
        toast({ kind: 'error', title: `${action.label} failed`, detail: e instanceof ApiError ? e.detail : String(e) });
        setPending(null);
        return undefined;
      } finally {
        setBusy(false);
      }
    },
    [api, resource, invalidate, navigate, toast, opts],
  );

  const run = useCallback(
    (action: ResolvedAction, ctx: RunContext = {}) => {
      if (action.confirm) return setPending({ action, ctx, stage: 'confirm' });
      if (action.input) return setPending({ action, ctx, stage: 'input' });
      void execute(action, ctx);
    },
    [execute],
  );

  const dialogs: ReactNode = pending ? (
    pending.stage === 'confirm' ? (
      <ConfirmDialog
        open
        spec={confirmSpec(pending.action)!}
        busy={busy}
        onClose={() => setPending(null)}
        onConfirm={(reason) => {
          if (pending.action.input) setPending({ ...pending, stage: 'input', reason });
          else void execute(pending.action, pending.ctx, reason ? { reason } : {});
        }}
      />
    ) : (
      <Dialog open onClose={() => setPending(null)} title={pending.action.label} locked={busy}>
        <InputForm schema={pending.action.input!} busy={busy} label={pending.action.label} onCancel={() => setPending(null)} onSubmit={(body) => void execute(pending.action, pending.ctx, pending.reason ? { ...body, reason: pending.reason } : body)} />
      </Dialog>
    )
  ) : null;

  return { run, dialogs, busy };
}

const TONE_VARIANT = { primary: 'primary', danger: 'danger', default: 'default' } as const;

/** Toolbar: the primary action as a button, the rest in a "More" menu (or all as buttons when few). */
export function ActionButtons({ actions, onRun, ctx, max = 2, size = 'md', extra }: { actions: ResolvedAction[]; onRun: (a: ResolvedAction, ctx: RunContext) => void; ctx: RunContext; max?: number; size?: 'sm' | 'md'; extra?: MenuItemDef[] }) {
  const [inline, overflow] = useMemo(() => {
    const sorted = [...actions].sort((a, b) => (b.tone === 'primary' ? 1 : 0) - (a.tone === 'primary' ? 1 : 0));
    return [sorted.slice(0, max), sorted.slice(max)];
  }, [actions, max]);
  const menu: MenuItemDef[] = [
    ...overflow.map((a) => ({ key: a.name, label: a.label, icon: a.icon as IconName | undefined, danger: a.tone === 'danger', onSelect: () => onRun(a, ctx) })),
    ...(extra?.length && overflow.length ? [{ separator: true, label: '' } as MenuItemDef] : []),
    ...(extra ?? []),
  ];
  return (
    <>
      {inline.map((a) =>
        a.component ? (
          <a.component key={a.name} resource="" id={ctx.id} row={ctx.row} refresh={ctx.refresh ?? (() => undefined)} selected={ctx.selected} />
        ) : (
          <Button key={a.name} size={size} variant={TONE_VARIANT[a.tone ?? 'default']} icon={a.icon as IconName | undefined} onClick={() => onRun(a, ctx)}>
            {a.label}
          </Button>
        ),
      )}
      {menu.length > 0 && <DropdownMenu aria-label="More actions" trigger={<Button size={size} variant="ghost" icon="more-horizontal" aria-label="More actions" />} items={menu} />}
    </>
  );
}
